import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, BookOpen, Users, Palette, FileText, CheckCircle } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const GENRES = ['Fiction', 'Non-Fiction', 'Mystery/Thriller', 'Romance', 'Sci-Fi/Fantasy', 'Biography/Memoir', 'Self-Help', 'Business', 'History', 'Children/YA', 'Poetry', 'Other'];
const AUDIENCES = ['General Adult', 'Young Adult (16-25)', 'Business Professionals', 'Academic', 'Children (6-12)', 'Teens (13-17)', 'Niche/Specialized'];
const TONES = ['Professional & Authoritative', 'Casual & Conversational', 'Literary & Poetic', 'Humorous & Light', 'Dark & Suspenseful', 'Inspirational & Motivational', 'Educational & Informative'];
const LENGTHS = ['Short (10,000-15,000 words)', 'Medium (15,000-30,000 words)', 'Full-Length (30,000-50,000 words)', 'Extended (50,000-80,000 words)', 'Not Sure Yet'];

const STEPS = [
  { id: 'genre', label: 'Genre & Style', icon: BookOpen },
  { id: 'audience', label: 'Audience & Tone', icon: Users },
  { id: 'details', label: 'Book Details', icon: FileText },
  { id: 'review', label: 'Review', icon: CheckCircle },
];

export default function OnboardingQuestionnaire() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('package') || '';
  const billingCycle = searchParams.get('billing') || 'annual';

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    genre: '', subgenre: '', target_audience: '', tone: '',
    book_length: '', has_outline: false, outline_summary: '',
    synopsis: '', special_requirements: '', reference_books: '', deadline_preference: '',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canProceed = () => {
    if (step === 0) return form.genre && form.book_length;
    if (step === 1) return form.target_audience && form.tone;
    if (step === 2) return form.synopsis.trim().length >= 20;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/ghostwriter/questionnaire', form);
      // Redirect to pricing with questionnaire ID
      navigate(`/pricing?q=${data.questionnaire_id}&package=${packageId}&billing=${billingCycle}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving your preferences');
    } finally { setLoading(false); }
  };

  return (
    <>
      <SeoHead
        title="Tell Us About Your Book — AI Ghostwriter Pro Onboarding"
        description="Share your book idea, genre, audience, and writing preferences. Our AI ghostwriters will craft a manuscript tailored to your vision."
        noindex={true}
      />

      <section className="pt-28 pb-20 md:pt-36 md:pb-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">

          {/* Progress */}
          <div className="flex items-center justify-between mb-10" data-testid="questionnaire-progress">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center border text-xs font-bold transition-all ${
                  i <= step ? 'bg-primary text-primary-foreground border-primary' : 'bg-white/5 text-muted-foreground border-white/10'
                }`}>
                  {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`w-8 sm:w-16 h-px mx-1 ${i < step ? 'bg-primary' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>

          <h1 className="font-serif text-2xl md:text-3xl tracking-tight mb-1 text-center" data-testid="questionnaire-title">
            {STEPS[step].label}
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">Step {step + 1} of {STEPS.length}</p>

          {/* Step 1: Genre */}
          {step === 0 && (
            <div className="space-y-6" data-testid="step-genre">
              <div>
                <label className="block text-sm font-semibold mb-3">What genre is your book?</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {GENRES.map(g => (
                    <button key={g} onClick={() => update('genre', g)} data-testid={`genre-${g}`}
                      className={`px-3 py-2.5 text-xs font-medium rounded-sm border transition-all ${form.genre === g ? 'bg-primary text-primary-foreground border-primary' : 'bg-white/5 border-white/10 text-muted-foreground hover:border-primary/30'}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              {form.genre && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Sub-genre or specific style (optional)</label>
                  <input type="text" placeholder="e.g., Cozy mystery, Epic fantasy, Self-help for entrepreneurs" value={form.subgenre} onChange={e => update('subgenre', e.target.value)}
                    data-testid="subgenre-input"
                    className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-3">Estimated book length</label>
                <div className="space-y-2">
                  {LENGTHS.map(l => (
                    <button key={l} onClick={() => update('book_length', l)} data-testid={`length-${l}`}
                      className={`w-full text-left px-4 py-3 text-sm rounded-sm border transition-all ${form.book_length === l ? 'bg-primary/10 border-primary/50 text-foreground' : 'bg-white/5 border-white/10 text-muted-foreground hover:border-primary/30'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Audience & Tone */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-audience">
              <div>
                <label className="block text-sm font-semibold mb-3">Who is your target audience?</label>
                <div className="grid grid-cols-2 gap-2">
                  {AUDIENCES.map(a => (
                    <button key={a} onClick={() => update('target_audience', a)} data-testid={`audience-${a}`}
                      className={`px-3 py-2.5 text-xs font-medium rounded-sm border transition-all ${form.target_audience === a ? 'bg-primary text-primary-foreground border-primary' : 'bg-white/5 border-white/10 text-muted-foreground hover:border-primary/30'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-3">What tone should the book have?</label>
                <div className="space-y-2">
                  {TONES.map(t => (
                    <button key={t} onClick={() => update('tone', t)} data-testid={`tone-${t}`}
                      className={`w-full text-left px-4 py-3 text-sm rounded-sm border transition-all ${form.tone === t ? 'bg-primary/10 border-primary/50 text-foreground' : 'bg-white/5 border-white/10 text-muted-foreground hover:border-primary/30'}`}>
                      <Palette className="w-3.5 h-3.5 inline mr-2" />{t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-details">
              <div>
                <label className="block text-sm font-semibold mb-2">Book synopsis or description <span className="text-muted-foreground font-normal">(min 20 chars)</span></label>
                <textarea value={form.synopsis} onChange={e => update('synopsis', e.target.value)} rows={4} required placeholder="Describe your book idea, main themes, plot points, or the message you want to convey..."
                  data-testid="synopsis-input"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm resize-none" />
                <p className="text-[10px] text-muted-foreground mt-1">{form.synopsis.length} characters</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <button onClick={() => update('has_outline', !form.has_outline)} data-testid="has-outline-toggle"
                    className={`w-10 h-6 rounded-full relative transition-all ${form.has_outline ? 'bg-primary' : 'bg-white/10'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${form.has_outline ? 'left-5' : 'left-1'}`} />
                  </button>
                  <label className="text-sm font-semibold">I have an outline or chapter structure</label>
                </div>
                {form.has_outline && (
                  <textarea value={form.outline_summary} onChange={e => update('outline_summary', e.target.value)} rows={3} placeholder="Paste or summarize your outline..."
                    data-testid="outline-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm resize-none" />
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Reference books or authors (optional)</label>
                <input type="text" value={form.reference_books} onChange={e => update('reference_books', e.target.value)} placeholder='e.g., "Write like Malcolm Gladwell" or "Similar to Atomic Habits"'
                  data-testid="reference-input"
                  className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Special requirements (optional)</label>
                <input type="text" value={form.special_requirements} onChange={e => update('special_requirements', e.target.value)} placeholder="Any specific formatting, research needs, or other requirements"
                  data-testid="special-input"
                  className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none text-sm" />
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-review">
              <div className="bg-white/5 border border-white/10 rounded-sm p-6 space-y-4">
                <ReviewItem label="Genre" value={`${form.genre}${form.subgenre ? ` — ${form.subgenre}` : ''}`} />
                <ReviewItem label="Audience" value={form.target_audience} />
                <ReviewItem label="Tone" value={form.tone} />
                <ReviewItem label="Book Length" value={form.book_length} />
                <ReviewItem label="Synopsis" value={form.synopsis} />
                {form.has_outline && <ReviewItem label="Outline" value={form.outline_summary || 'Provided'} />}
                {form.reference_books && <ReviewItem label="References" value={form.reference_books} />}
                {form.special_requirements && <ReviewItem label="Special Requirements" value={form.special_requirements} />}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10" data-testid="questionnaire-nav">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} data-testid="prev-step-btn"
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border border-white/20 rounded-sm text-foreground hover:border-primary hover:text-primary transition-all">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}
            {step < STEPS.length - 1 ? (
              <button onClick={() => canProceed() && setStep(step + 1)} disabled={!canProceed()} data-testid="next-step-btn"
                className={`flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-sm transition-all ${canProceed() ? 'bg-primary text-primary-foreground hover:bg-white hover:text-black shadow-[0_0_10px_rgba(212,175,55,0.2)]' : 'bg-white/10 text-muted-foreground cursor-not-allowed'}`}>
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} data-testid="submit-questionnaire-btn"
                className="flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-sm bg-primary text-primary-foreground hover:bg-white hover:text-black transition-all shadow-[0_0_15px_rgba(212,175,55,0.3)]">
                {loading ? 'Saving...' : <>Continue to Checkout <ArrowRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
